import React, { useMemo } from 'react';

import { get, isEmpty } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Input, Slider } from 'antd';
import { SliderMarks } from 'antd/lib/slider';
import { RootState, GetWalletNumber, getWalletSliderMaxValuePosition } from '@share/utils';

import './style.scss';

const NUMBER_REGEX = /[0-9]/;
const NUMBER_DECIMAL_REGEX = /[0-9.]/;

interface IProps {
  display: boolean;
  bottomBorder?: boolean;

  currency: string | null | undefined;

  maxWalletClientCash: number;
  price: number;
  selectedClientCash: number | null | undefined;
  selectedClientCashStr: string;

  onSelectClientCash: (selectedClientCash: string | null | undefined) => void;
}

export function WalletInput(props: IProps): React.ReactElement | null {
  const {
    display,
    bottomBorder,
    maxWalletClientCash,
    selectedClientCash,
    selectedClientCashStr,
    price,
    currency,
    onSelectClientCash,
  } = props;

  const loginStore = useSelector((state: RootState) => state.loginStore);
  const { account, userWalletData } = loginStore;
  const intl = useIntl();

  const walletNoPassEarnings = account?.walletNoPassEarnings;
  const walletUseSlider = account?.walletUseSlider;
  const walletNoDecimals = account?.walletNoDecimals;
  const background = account?.buttonColor ? account?.buttonColor : '#0081FE';

  const convertionRate = userWalletData?.convertionRate ? userWalletData?.convertionRate : 1;
  const maxWalletClientCashCalculated = maxWalletClientCash ? walletNoDecimals ? Math.floor(maxWalletClientCash) : maxWalletClientCash : null;
  const userWalletDataBalanceCalculated = (walletNoDecimals && userWalletData?.balance) ? Math.floor(userWalletData?.balance) : userWalletData?.balance;
  const priceCalculated = price / convertionRate;

  const maxWalletAmountIntermediated = walletNoPassEarnings ?
                                          maxWalletClientCashCalculated ?
                                            (maxWalletClientCashCalculated && (userWalletDataBalanceCalculated || 0) > maxWalletClientCashCalculated) ?
                                              maxWalletClientCashCalculated :
                                              userWalletDataBalanceCalculated :
                                            0 :
                                          (userWalletDataBalanceCalculated || 0) > priceCalculated ?
                                            priceCalculated :
                                            userWalletDataBalanceCalculated;
  const maxWalletAmount = (maxWalletClientCashCalculated && (maxWalletAmountIntermediated || 0) > maxWalletClientCashCalculated) ? maxWalletClientCashCalculated : maxWalletAmountIntermediated;

  const maxSlider = maxWalletAmount ?
                      walletNoDecimals ?
                        Math.floor(maxWalletAmount) :
                        maxWalletAmount ? Math.floor(maxWalletAmount * 100) / 100 : 0 :
                    0;

  // Memoize marks so its identity is stable across renders. rc-slider derives its internal
  // `rawValues` from the `marks` object identity; a brand-new object on every render makes its
  // cache-sync effect re-fire each render, which can spiral into React error #185
  // ("Maximum update depth exceeded") while dragging the slider.
  const marks: SliderMarks = useMemo(() => {
    const result: SliderMarks = {
      0: {
        style: {
          transform: 'translate(-4px, -40px)'
        },
        label: '0',
      },
    };

    if (maxSlider) {
      result[maxSlider] = {
        style: {
          transform: `translate(${walletNoDecimals ? getWalletSliderMaxValuePosition(maxSlider) :'-27'}px, -40px)`
        },
        label: `${maxSlider?.toLocaleString('en-US')}`,
      };
    }

    return result;
  }, [maxSlider, walletNoDecimals]);

  if (!display) {
    return null;
  }

  // The stored client-cash string can carry full precision (e.g. "82.7744633") when it comes from
  // the slider max or an auto-fill, but the input must mirror the slider end: at most two decimals,
  // truncated — never rounded. Typed values are already capped at two decimals by onKeyPress, so
  // this only trims programmatic values. When decimals aren't allowed we keep the integer part.
  const truncateClientCashStr = (str: string): string => {
    if (isEmpty(str)) return str;
    const dot = str.indexOf('.');
    if (dot === -1) return str;
    return walletNoDecimals ? str.slice(0, dot) : str.slice(0, dot + 1) + str.slice(dot + 1).slice(0, 2);
  };

  const inputValue = (selectedClientCash && selectedClientCash >= 0) ? truncateClientCashStr(selectedClientCashStr) : '';

  return (
    <div className={`wallet-input ${bottomBorder ? 'border-bm' : ''}`}>
      <div className="wallet-input__note">
        <p className="wallet-input__price-info-text-total">
          {isEmpty(account?.walletClientCashName) ? (<FormattedMessage id="wallet.client.cash" />) : account?.walletClientCashName}:
        </p>
      </div>

      <div className="wallet-input__note" style={{ marginBottom: '10px', whiteSpace: 'unset' }}>
        {maxWalletAmount ? (
          <FormattedMessage id="wallet.client.cash.message" values={{ amount: maxSlider?.toLocaleString('en-US'), amountActive: GetWalletNumber(userWalletData?.balance, account), clientCash: isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName }} />) : (
          <FormattedMessage id="wallet.client.cash.no_client_cash.message" values={{ amountActive: GetWalletNumber(userWalletData?.balance, account), clientCash: isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName }} />)}
      </div>

      <div className="wallet-input__note">
        {!walletUseSlider ? (
          <input type="number" value={(selectedClientCash && selectedClientCash >= 0) ? selectedClientCash : '' }
            className={`wallet-input__client-cash-input ${(selectedClientCash || 0) > (maxWalletAmount || 0) ? 'invalid' : ''}`}
            onChange={e => {
              const value = !isEmpty(e.target.value) ? e.target.value : null;
              onSelectClientCash(value || null);
            }}
            placeholder={intl.formatMessage({ id: 'wallet.client.cash.placeholder' }, { clientCash: !isEmpty(account?.walletClientCashName) ? account?.walletClientCashName : 'Client Cash'})}
            disabled={(maxWalletAmount || 0) <= 0}
          />) : (
          <div className="room-info__slider">
            <Slider
              min={0}
              max={maxSlider}
              marks={marks}
              defaultValue={0}
              step={walletNoDecimals ? 1 : 0.01}
              tooltip={{ open: false }}
              value={(selectedClientCash || 0) >= 0 ? (selectedClientCash || 0) : 0}
              onChange={(value: any) => onSelectClientCash(value)}
              trackStyle={{ backgroundColor: background }}
              handleStyle={{ borderColor: background, backgroundColor: background }}
              disabled={(maxWalletAmount || 0) <= 0}
            />
            <Input value={inputValue}
              className={`room-info__client-cash-input ${(selectedClientCash || 0) > (maxWalletAmount || 0) ? 'invalid' : ''}`}
              onChange={e => {
                const value = !isEmpty(e.target.value) ?
                                    walletNoDecimals ?
                                        e.target.value.replace(',', '').replace('.', '') :
                                        e.target.value.replace(',', '') :
                                    null;

                onSelectClientCash(value || null);
              }}
              onKeyPress={(event) => {
                const REGEX = walletNoDecimals ? NUMBER_REGEX : NUMBER_DECIMAL_REGEX;

                const decimals = get(inputValue?.split('.'), '[1]', '');

                if (!REGEX.test(event.key) || (event.key === '.' && inputValue.includes('.')) || decimals?.length >= 2) {
                  event.preventDefault();
                }
              }}
              disabled={(maxWalletAmount || 0) <= 0}
            />
          </div>)}
      </div>

      {(selectedClientCash || 0) > (maxWalletAmount || 0) && (
        <div className="wallet-input__note invalid" style={{ marginBottom: '10px' }}>
          <FormattedMessage id="wallet.client.cash.invalid" values={{ amount: GetWalletNumber(maxWalletAmount || 0, account), currency: currency, clientCash: isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName }} />
        </div>)}

      {(!!selectedClientCash && walletNoDecimals && (selectedClientCash || 0) <= (maxWalletAmount || 0) && Math.floor((selectedClientCash || 0)) != (selectedClientCash || 0)) && (
        <div className="wallet-input__note invalid" style={{ marginBottom: '10px' }}>
          <FormattedMessage id="wallet.client.cash.only_decimals" values={{ clientCash: isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName }} />
        </div>)}
    </div>
  );
}
